import React, { memo } from "react";

import { EntityType } from "@mapmycustomers/shared";
import {
  Company,
  Deal,
  EntityTypesSupportingRoutes,
  Person,
  Route,
} from "@mapmycustomers/shared/types/entity";
import { isMapped } from "@mapmycustomers/shared/util/assert";

import RelationshipType from "@app/component/preview/enum/RelationshipType";

import IRelationships from "../../type/IRelationships";

import ChildCompanies from "./ChildCompanies";
import Deals from "./Deals";
import ParentCompanies from "./ParentCompanies";
import ParentCompany from "./ParentCompany";
import People from "./People";
import Routes from "./Routes";

interface Props
  extends Omit<
    IRelationships,
    "onRemoveChildCompany" | "onRemoveDeal" | "onRemoveRoute" | "supportedRelationshipTypes"
  > {
  childCompaniesFiltered: Company[];
  companies?: Company[];
  dealsFiltered: Deal[];
  isSearchEmpty: boolean;
  onAddNewRecord: (type: RelationshipType) => void;
  onChangePrimaryCompany?: () => void;
  onChangePrimaryPerson?: () => void;
  onChangeType: (type: RelationshipType) => void;
  onFetchMoreCompanies?: () => void;
  onFetchMoreDeals?: () => void;
  onRemoveChildCompany: (company: Company) => void;
  onRemoveDeal: (deal: Deal) => void;
  onRemoveRoute: (route: Route) => void;
  parentCompaniesFiltered: Company[];
  peopleFiltered: Person[];
  peopleMultiSelect: boolean;
  routesFiltered: Route[];
  supportedRelationshipTypes: RelationshipType[];
  totalCompanies?: number;
  totalDeals?: number;
}

const Lists: React.FC<Props> = ({
  addChildCompaniesLoading,
  addDealsLoading,
  addParentCompanyLoading,
  addPersonsLoading,
  addRoutesLoading,
  childCompaniesFiltered = [],
  companies,
  dealsFiltered,
  entity,
  isSearchEmpty,
  onAddNewRecord,
  onChangePrimaryCompany,
  onChangePrimaryPerson,
  onChangeType,
  onFetchMoreCompanies,
  onFetchMoreDeals,
  onFetchMorePeople,
  onRemoveChildCompany,
  onRemoveDeal,
  onRemoveParentCompany,
  onRemovePeople,
  onRemoveRoute,
  onUnsetPrimaryCompany,
  onUnsetPrimaryPerson,
  parentCompaniesFiltered,
  peopleFiltered,
  peopleMultiSelect,
  removeRouteLoadingIds,
  routesFiltered = [],
  supportedRelationshipTypes,
  totalCompanies,
  totalDeals,
  totalPeople,
}) => {
  const hasLocation = isMapped(entity);

  return (
    <>
      {(parentCompaniesFiltered.length > 0 || isSearchEmpty) &&
        supportedRelationshipTypes.includes(RelationshipType.PARENT_COMPANY) &&
        (entity.entity === EntityType.PERSON ? (
          <ParentCompanies
            addCompanyParentCompaniesLoading={addParentCompanyLoading}
            companies={companies ?? []}
            onAddExisting={() => onChangeType(RelationshipType.PARENT_COMPANY)}
            onAddNew={() => onAddNewRecord(RelationshipType.PARENT_COMPANY)}
            onChangePrimaryCompany={onChangePrimaryCompany}
            onFetchMoreCompanies={onFetchMoreCompanies}
            onRemove={onRemoveParentCompany}
            onUnsetPrimaryCompany={onUnsetPrimaryCompany}
            parentCompaniesFiltered={parentCompaniesFiltered}
            primaryCompanyId={entity.primaryAccountId ?? undefined}
            totalCompanies={totalCompanies ?? 0}
          />
        ) : (
          <ParentCompany
            addCompanyParentCompanyLoading={addParentCompanyLoading}
            company={parentCompaniesFiltered[0]}
            entityType={entity.entity}
            onAddExisting={() => onChangeType(RelationshipType.PARENT_COMPANY)}
            onAddNew={() => onAddNewRecord(RelationshipType.PARENT_COMPANY)}
            onFetchMoreCompanies={onFetchMoreCompanies}
            onRemove={onRemoveParentCompany}
            totalCompanies={totalCompanies ?? 0}
          />
        ))}
      {(childCompaniesFiltered.length > 0 || isSearchEmpty) &&
        supportedRelationshipTypes.includes(RelationshipType.CHILD_COMPANY) && (
          <ChildCompanies
            addChildCompanyLoading={addChildCompaniesLoading}
            childCompanies={childCompaniesFiltered}
            onAddExisting={() => onChangeType(RelationshipType.CHILD_COMPANY)}
            onAddNew={() => onAddNewRecord(RelationshipType.CHILD_COMPANY)}
            onFetchMoreCompanies={onFetchMoreCompanies}
            onRemove={onRemoveChildCompany}
            totalCompanies={totalCompanies ?? 0}
          />
        )}
      {(peopleFiltered.length > 0 || isSearchEmpty) &&
        supportedRelationshipTypes.includes(RelationshipType.PEOPLE) && (
          <People
            addPersonLoading={addPersonsLoading}
            entityType={entity.entity}
            onAddExisting={() => onChangeType(RelationshipType.PEOPLE)}
            onAddNew={() => onAddNewRecord(RelationshipType.PEOPLE)}
            onChangePrimaryPerson={
              entity.entity === EntityType.COMPANY ? onChangePrimaryPerson : undefined
            }
            onFetchMorePeople={onFetchMorePeople}
            onRemove={onRemovePeople}
            onUnsetPrimaryPerson={
              entity.entity === EntityType.COMPANY ? onUnsetPrimaryPerson : undefined
            }
            people={peopleFiltered}
            peopleMultiSelect={peopleMultiSelect}
            primaryPersonId={
              entity.entity === EntityType.COMPANY && entity.primaryContactId
                ? entity.primaryContactId
                : undefined
            }
            totalPeople={totalPeople ?? 0}
          />
        )}
      {(dealsFiltered.length > 0 || isSearchEmpty) &&
        supportedRelationshipTypes.includes(RelationshipType.DEALS) && (
          <Deals
            addDealLoading={addDealsLoading}
            deals={dealsFiltered}
            onAddExisting={() => onChangeType(RelationshipType.DEALS)}
            onAddNew={() => onAddNewRecord(RelationshipType.DEALS)}
            onFetchMoreDeals={onFetchMoreDeals}
            onRemove={onRemoveDeal}
            totalDeals={totalDeals ?? 0}
          />
        )}
      {(routesFiltered.length > 0 || isSearchEmpty) &&
        supportedRelationshipTypes.includes(RelationshipType.ROUTES) && (
          <Routes
            entityType={entity.entity as EntityTypesSupportingRoutes}
            hasLocation={hasLocation}
            onAdd={() => onChangeType(RelationshipType.ROUTES)}
            onRemove={onRemoveRoute}
            removeRouteLoadingIds={removeRouteLoadingIds}
            routes={routesFiltered}
            routesLoading={addRoutesLoading}
          />
        )}
    </>
  );
};

export default memo(Lists);
